import { Injectable, OnDestroy } from '@angular/core';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { ClipViewV2 } from '../models/user';
import { ApiHelperService } from './api-helper.service';
import { ClipviewsService } from './clipviews.service';
import { LocalStorageService } from './local-storage.service';

interface QueuedClipView {
  event: ClipViewV2;
  queuedAt: string;
  windowStartedAt?: string;
  attemptedAt?: string | null;
}

interface QueueLease {
  owner: string;
  expiresAt: number;
}

interface BrowserLockManager {
  request<T>(name: string, options: {mode: 'exclusive', ifAvailable?: boolean}, callback: (lock: unknown | null) => T | Promise<T>): Promise<T>;
}

@Injectable({providedIn: 'root'})
export class ClipViewQueueService implements OnDestroy {
  private readonly maxQueueSize = 2000;
  private readonly maxBatchSize = 100;
  private readonly maxEventDurationMs = 30000;
  private readonly maxQueueAgeMs = 7 * 24 * 60 * 60 * 1000;
  private readonly maxRetryMs = 15 * 60 * 1000;
  private readonly batchFlushIntervalMs = 30000;
  private readonly submissionLeaseMs = 60000;
  private readonly mutationLeaseMs = 2000;
  private readonly tabId = this.createEventId();
  private flushing = new Set<string>();
  private batchFlushTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private retryAttempts = new Map<string, number>();
  private retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly onlineListener = () => this.flushCurrentUser();
  private readonly storageListener = (event: StorageEvent) => this.onQueueStorageChanged(event);
  private readonly userUpdateSubscription: Subscription;

  constructor(
    private apiHelper: ApiHelperService,
    private clipviewsService: ClipviewsService,
    private localStorageService: LocalStorageService,
  ) {
    window.addEventListener('online', this.onlineListener);
    window.addEventListener('storage', this.storageListener);
    this.userUpdateSubscription = this.localStorageService.listenForUserUpdate().subscribe(user => {
      if (user?._id) this.flushCurrentUser();
    });
  }

  ngOnDestroy() {
    window.removeEventListener('online', this.onlineListener);
    window.removeEventListener('storage', this.storageListener);
    this.userUpdateSubscription.unsubscribe();
    this.batchFlushTimers.forEach(timer => clearTimeout(timer));
    this.retryTimers.forEach(timer => clearTimeout(timer));
    this.batchFlushTimers.clear();
    this.retryTimers.clear();
  }

  createEventId(): string {
    const cryptoApi: Crypto = window.crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const value = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
  }

  async enqueue(event: ClipViewV2, flushImmediately: boolean = false): Promise<void> {
    if (!event?.userid || !event.teamid || !event.eventid) return;
    await this.withQueueMutationLock(event.userid, event.teamid, () => {
      const queue = this.readQueue(event.userid, event.teamid);
      if (_.some(queue, item => item.event.eventid === event.eventid)) return;
      this.appendToCurrentWindow(queue, event);
      if (queue.length > this.maxQueueSize) {
        const removed = queue.splice(0, queue.length - this.maxQueueSize);
        console.log('Clip view queue reached its size limit', {userid: event.userid, teamid: event.teamid, removed: removed.length});
      }
      this.writeQueue(event.userid, event.teamid, queue);
    });

    this.ensureSubmissionLeader(event.userid, event.teamid);
    const queueLength = this.readQueue(event.userid, event.teamid).length;
    if (flushImmediately || queueLength >= this.maxBatchSize) void this.flush(event.userid, event.teamid);
    else this.scheduleBatchFlush(event.userid, event.teamid);
  }

  enqueueSynchronously(event: ClipViewV2) {
    if (!event?.userid || !event.teamid || !event.eventid) return;
    const leaseKey = `clipViewMutationLease:${event.userid}:${event.teamid}`;
    const acquired = this.acquireLease(leaseKey, this.mutationLeaseMs);
    try {
      const queue = this.readQueue(event.userid, event.teamid);
      if (!_.some(queue, item => item.event.eventid === event.eventid)) {
        this.appendToCurrentWindow(queue, event);
        if (queue.length > this.maxQueueSize) queue.splice(0, queue.length - this.maxQueueSize);
        this.writeQueue(event.userid, event.teamid, queue);
      }
      this.ensureSubmissionLeader(event.userid, event.teamid);
    }
    finally {
      if (acquired) this.releaseLease(leaseKey);
    }
  }

  flushCurrentUser() {
    const user = this.localStorageService.getUser();
    if (!user?._id) return;
    const teamids = _.compact(_.uniq(_.map(user.userRoles || [], 'teamid')));
    _.forEach(teamids, teamid => void this.flush(user._id, teamid));
  }

  async flush(userid: string, teamid: string): Promise<void> {
    if (!userid || !teamid || !navigator.onLine) return;
    const key = `${userid}:${teamid}`;
    if (this.flushing.has(key)) return;
    this.clearBatchFlush(key);
    const user = this.localStorageService.getUser();
    const authorized = user?._id === userid && _.some(user.userRoles || [], {teamid});
    if (!authorized) return;
    if (!this.ensureSubmissionLeader(userid, teamid)) {
      this.scheduleBatchFlush(userid, teamid);
      return;
    }

    this.flushing.add(key);
    let acquired = false;
    try {
      acquired = await this.withBrowserFlushLock(key, async () => {
        let batch: QueuedClipView[] = [];
        await this.withQueueMutationLock(userid, teamid, () => {
          const queue = this.readQueue(userid, teamid);
          batch = queue.slice(0, this.maxBatchSize);
          if (!batch.length) return;
          const attemptedAt = new Date().toISOString();
          const attemptedIds = new Set(_.map(batch, item => item.event.eventid));
          _.forEach(queue, item => {
            if (attemptedIds.has(item.event.eventid)) item.attemptedAt = item.attemptedAt || attemptedAt;
          });
          this.writeQueue(userid, teamid, queue);
        });
        if (!batch.length) {
          this.releaseSubmissionLeadership(userid, teamid);
          return;
        }

        let remainingCount = 0;
        try {
          const response = await this.clipviewsService.postClipViews(_.map(batch, 'event'));
          if (!response.success) throw new Error('Clip view request was not successful');
          const accepted = response.result?.acceptedEventids || _.map(batch, item => item.event.eventid);
          const rejected = _.compact(_.map(response.result?.rejected || [], rejection => rejection.eventid));
          if (rejected.length) console.log('Clip view events rejected', response.result.rejected);
          const completed = new Set([...accepted, ...rejected]);
          if (!completed.size) throw new Error('Clip view response did not acknowledge any events');
          await this.withQueueMutationLock(userid, teamid, () => {
            const remaining = this.readQueue(userid, teamid).filter(item => !completed.has(item.event.eventid));
            remainingCount = remaining.length;
            this.writeQueue(userid, teamid, remaining);
          });
          this.retryAttempts.delete(key);
          this.clearRetry(key);
          if (!remainingCount) this.releaseSubmissionLeadership(userid, teamid);
        }
        catch (err) {
          console.log('Unable to submit queued clip views', err);
          this.scheduleRetry(userid, teamid);
        }
        if (remainingCount >= this.maxBatchSize) setTimeout(() => void this.flush(userid, teamid), 1000);
        else if (remainingCount) this.scheduleBatchFlush(userid, teamid);
      });
    }
    catch (err) {
      console.log('Unable to coordinate queued clip views', err);
      this.scheduleRetry(userid, teamid);
    }
    finally {
      this.flushing.delete(key);
    }

    if (!acquired) this.scheduleBatchFlush(userid, teamid);
  }

  flushWithKeepalive(userid: string, teamid: string) {
    if (!this.ownsSubmissionLeadership(userid, teamid)) return;
    const mutationLeaseKey = `clipViewMutationLease:${userid}:${teamid}`;
    if (!this.acquireLease(mutationLeaseKey, this.mutationLeaseMs)) return;
    const queue = this.readQueue(userid, teamid);
    const batch = queue.slice(0, this.maxBatchSize);
    try {
      if (!batch.length || typeof fetch !== 'function') return;
      const attemptedAt = new Date().toISOString();
      const attemptedIds = new Set(_.map(batch, item => item.event.eventid));
      _.forEach(queue, item => {
        if (attemptedIds.has(item.event.eventid)) item.attemptedAt = item.attemptedAt || attemptedAt;
      });
      this.writeQueue(userid, teamid, queue);
      const token = this.localStorageService.getToken();
      fetch(`${this.apiHelper.apiUrl()}/view-stats`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', ...(token ? {Authorization: token} : {})},
        body: JSON.stringify({clipviews: _.map(batch, 'event')}),
        keepalive: true,
      }).catch(() => undefined);
    }
    finally {
      this.releaseLease(mutationLeaseKey);
    }
  }

  private appendToCurrentWindow(queue: QueuedClipView[], event: ClipViewV2) {
    const now = new Date();
    const activeWindow = _.findLast(queue, item => item.attemptedAt === null
      && !!item.windowStartedAt
      && now.getTime() - new Date(item.windowStartedAt).getTime() < this.batchFlushIntervalMs);
    const windowStartedAt = activeWindow?.windowStartedAt || now.toISOString();
    let remainingDurationMs = event.durationMs;
    let consumedDurationMs = 0;

    while (remainingDurationMs > 0) {
      const aggregate = _.findLast(queue, item => item.attemptedAt === null
        && item.windowStartedAt === windowStartedAt
        && item.event.userid === event.userid
        && item.event.teamid === event.teamid
        && item.event.gameid === event.gameid
        && item.event.clipnumber === event.clipnumber
        && item.event.source === event.source
        && item.event.sourceVersion === event.sourceVersion
        && item.event.durationMs < this.maxEventDurationMs);
      const availableDurationMs = aggregate ? this.maxEventDurationMs - aggregate.event.durationMs : 0;
      if (aggregate && availableDurationMs > 0) {
        const addedDurationMs = Math.min(remainingDurationMs, availableDurationMs);
        aggregate.event.durationMs += addedDurationMs;
        aggregate.event.end = new Date(new Date(aggregate.event.start).getTime() + aggregate.event.durationMs);
        remainingDurationMs -= addedDurationMs;
        consumedDurationMs += addedDurationMs;
        continue;
      }

      const durationMs = Math.min(remainingDurationMs, this.maxEventDurationMs);
      const start = new Date(new Date(event.start).getTime() + consumedDurationMs);
      queue.push({
        event: {
          ...event,
          eventid: consumedDurationMs ? this.createEventId() : event.eventid,
          start,
          end: new Date(start.getTime() + durationMs),
          durationMs,
        },
        queuedAt: now.toISOString(),
        windowStartedAt,
        attemptedAt: null,
      });
      remainingDurationMs -= durationMs;
      consumedDurationMs += durationMs;
    }
  }

  private readQueue(userid: string, teamid: string): QueuedClipView[] {
    const cutoff = Date.now() - this.maxQueueAgeMs;
    const queue = this.localStorageService.getClipViewQueue<QueuedClipView>(userid, teamid);
    const valid = _.filter(queue, item => item?.event?.eventid && new Date(item.queuedAt).getTime() >= cutoff);
    if (valid.length !== queue.length) {
      console.log('Expired queued clip views removed', {userid, teamid, removed: queue.length - valid.length});
    }
    return valid;
  }

  private writeQueue(userid: string, teamid: string, queue: QueuedClipView[]) {
    if (queue.length) this.localStorageService.setClipViewQueue(userid, teamid, queue);
    else this.localStorageService.removeClipViewQueue(userid, teamid);
  }

  private async withQueueMutationLock<T>(userid: string, teamid: string, callback: () => T | Promise<T>): Promise<T> {
    const name = `qwikcut-clipview-mutation:${userid}:${teamid}`;
    const locks = (navigator as Navigator & {locks?: BrowserLockManager}).locks;
    const leaseKey = `clipViewMutationLease:${userid}:${teamid}`;
    if (locks?.request) {
      return locks.request(name, {mode: 'exclusive'}, async () => {
        while (!this.acquireLease(leaseKey, this.mutationLeaseMs)) {
          await new Promise(resolve => setTimeout(resolve, 20));
        }
        try {
          return await callback();
        }
        finally {
          this.releaseLease(leaseKey);
        }
      });
    }

    while (!this.acquireLease(leaseKey, this.mutationLeaseMs)) {
      await new Promise(resolve => setTimeout(resolve, 20));
    }
    try {
      return await callback();
    }
    finally {
      this.releaseLease(leaseKey);
    }
  }

  private async withBrowserFlushLock(key: string, callback: () => Promise<void>): Promise<boolean> {
    const locks = (navigator as Navigator & {locks?: BrowserLockManager}).locks;
    if (!locks?.request) {
      await callback();
      return true;
    }
    let acquired = false;
    await locks.request(`qwikcut-clipview-flush:${key}`, {mode: 'exclusive', ifAvailable: true}, async lock => {
      if (!lock) return;
      acquired = true;
      await callback();
    });
    return acquired;
  }

  private ensureSubmissionLeader(userid: string, teamid: string): boolean {
    const leaseKey = this.getSubmissionLeaseKey(userid, teamid);
    const lease = this.readLease(leaseKey);
    if (lease?.owner === this.tabId || !lease || lease.expiresAt <= Date.now()) {
      return this.acquireLease(leaseKey, this.submissionLeaseMs);
    }
    return false;
  }

  private ownsSubmissionLeadership(userid: string, teamid: string): boolean {
    const lease = this.readLease(this.getSubmissionLeaseKey(userid, teamid));
    return lease?.owner === this.tabId && lease.expiresAt > Date.now();
  }

  private releaseSubmissionLeadership(userid: string, teamid: string) {
    this.releaseLease(this.getSubmissionLeaseKey(userid, teamid));
  }

  private acquireLease(key: string, durationMs: number): boolean {
    const existing = this.readLease(key);
    if (existing && existing.owner !== this.tabId && existing.expiresAt > Date.now()) return false;
    const lease: QueueLease = {owner: this.tabId, expiresAt: Date.now() + durationMs};
    localStorage.setItem(key, JSON.stringify(lease));
    const confirmed = this.readLease(key);
    return confirmed?.owner === this.tabId;
  }

  private readLease(key: string): QueueLease | null {
    try {
      return JSON.parse(localStorage.getItem(key) || 'null');
    }
    catch (_) {
      return null;
    }
  }

  private releaseLease(key: string) {
    const lease = this.readLease(key);
    if (lease?.owner === this.tabId) localStorage.removeItem(key);
  }

  private getSubmissionLeaseKey(userid: string, teamid: string) {
    return `clipViewSubmissionLease:${userid}:${teamid}`;
  }

  private onQueueStorageChanged(event: StorageEvent) {
    if (!event.key?.startsWith('clipViewQueue:')) return;
    const [, userid, teamid] = event.key.split(':');
    if (userid && teamid && this.ownsSubmissionLeadership(userid, teamid)) this.scheduleBatchFlush(userid, teamid);
  }

  private scheduleRetry(userid: string, teamid: string) {
    const key = `${userid}:${teamid}`;
    if (this.retryTimers.has(key)) return;
    const attempt = (this.retryAttempts.get(key) || 0) + 1;
    this.retryAttempts.set(key, attempt);
    const delay = Math.min(30000 * Math.pow(2, attempt - 1), this.maxRetryMs);
    this.retryTimers.set(key, setTimeout(() => {
      this.retryTimers.delete(key);
      void this.flush(userid, teamid);
    }, delay));
  }

  private scheduleBatchFlush(userid: string, teamid: string) {
    const key = `${userid}:${teamid}`;
    if (this.batchFlushTimers.has(key)) return;
    this.batchFlushTimers.set(key, setTimeout(() => {
      this.batchFlushTimers.delete(key);
      void this.flush(userid, teamid);
    }, this.batchFlushIntervalMs));
  }

  private clearBatchFlush(key: string) {
    const timer = this.batchFlushTimers.get(key);
    if (timer) clearTimeout(timer);
    this.batchFlushTimers.delete(key);
  }

  private clearRetry(key: string) {
    const timer = this.retryTimers.get(key);
    if (timer) clearTimeout(timer);
    this.retryTimers.delete(key);
  }
}
